import Container from 'typedi';
import { ILogger, LoggerProvider } from '../logger';

import { IPostMessageHandler } from '../post-message';
import { MembershipActions } from '../../models/enums/Consts';
import { NativeUCAppType } from '../player/enums/consts';
import { PlayerService } from '../player/player.service';
import PostLoginApi from '../../../APIs/PostLoginApi';
import { RegistrationAuthenticationService } from '../authentication/services/registration-authentication.service';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { Utils } from '../utils';
import { WebLoginService } from '../web-login/web.login.service';
import { CommonService } from '../external/clients-framework';
import { SessionSimpleStoreService } from '../storage/implementations/simple-store';
import { ILoginOptions } from '../authentication/models';
import { IActionChannel } from '../post-login/models/IPostLoginActionsState';
import { NativeUtils } from '../native/native.utils';

import { Navigation } from '../navigation';

export class RegistrationHandler implements IPostMessageHandler {
    private _logger: ILogger;
    private _sessionSimpleStoreService: SessionSimpleStoreService;
    private _webLoginService: WebLoginService;
    private _utils: Utils;
    private _playerService: PlayerService;
    private _registrationAuthenticationService: RegistrationAuthenticationService;
    private _cfCommonService: CommonService;

    protected readonly _navigation: Navigation;

    constructor() {
        this._cfCommonService = Container.get(CommonService);
        this._logger = Container.get(LoggerProvider).getLogger(this.name);
        this._playerService = Container.get(PlayerService);
        this._registrationAuthenticationService = Container.get(RegistrationAuthenticationService);
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._utils = Container.get(Utils);
        this._webLoginService = Container.get(WebLoginService);
        this._navigation = Container.get(Navigation);
    }

    name: string = 'RegistrationPostMessageHandler';

    public handler = async (event: any): Promise<boolean> => {
        return this._handleMessages(event);
    };

    private _handleMessages = async (event: MessageEvent): Promise<boolean> => {
        this._logger.log(`Handler RegistrationPostMessageHandler is going to be executed...`);

        if (!event.data) return false;

        const message = this._utils.TryParseJSON(event.data);

        if (!message) return false;
        if (!message.ActionId) return false;

        switch (message.ActionId) {
            case MembershipActions.Autologin: {
                this._checkLowFunds().then(() => {
                    this._autoLogin(message);
                });

                break;
            }
            case MembershipActions.Deposit: {
                if (message.UserInfo) {
                    const channel: IActionChannel = {
                        Source: 'Registration',
                        Area: 'Straight to Cashier',
                        Element: 'UD',
                    };

                    this._sessionSimpleStoreService.set(
                        StorageItemEnum.CashierOpenStartTimestamp,
                        String(Date.now()),
                    );

                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            OpenCashier: {
                                channel,
                                enabled: true,
                            },
                        },
                    };
                    this._autoLogin(message, loginOptions);
                }

                break;
            }
            case MembershipActions.UploadDocument: {
                if (message.UserInfo) {
                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            OpenCashierUploadDocument: {
                                enabled: true,
                            },
                        },
                    };

                    this._autoLogin(message, loginOptions);
                }
                break;
            }
            case MembershipActions.ShowWebLogin: {
                NativeUtils.showBottomBar(true);
                this._cfCommonService.CloseWrapDiv();
                this._webLoginService.Show();

                break;
            }
            case MembershipActions.Back:
            case MembershipActions.CloseWindow: {
                NativeUtils.showBottomBar(true);
                this._cfCommonService.CloseWrapDiv();
                break;
            }
            case MembershipActions.BankId: {
                if (message.AppUrl) {
                    this._cfCommonService.openMobileAppWithFallback(
                        message.AppUrl,
                        'https://play.google.com/store/apps/details?id=com.bankid.bus',
                        'https://apps.apple.com/se/app/bankid-s%C3%A4kerhetsapp/id433151512?l=en',
                    );
                }
                break;
            }
            case MembershipActions.StoreRegistrationCookiesData: {
                NativeUtils.showBottomBar(false);

                if (message.StoreRegistrationCookiesData) {
                    localStorage.setItem(
                        'registration_data',
                        JSON.stringify(message.StoreRegistrationCookiesData),
                    );
                }
                break;
            }
            case MembershipActions.DownloadNativeSportApp: {
                this._playerService.OpenNativeApp({ Type: NativeUCAppType.Sport888 });
                break;
            }
            case MembershipActions.ShowKYC: {
                if (message.UserInfo) {
                    const loginOptions: ILoginOptions = {
                        postLogin: {
                            ShowKYC: {
                                enabled: true,
                            },
                        },
                    };

                    this._autoLogin(message, loginOptions);
                }
                break;
            }
            case MembershipActions.RedirectClient: {
                if (message.Url)
                    this._navigation.redirectOrNativeOpenExternalUrl({ url: message.Url });
                break;
            }
        }

        return true;
    };

    private _checkLowFunds = async () => {
        const { response, errorResponse } = await PostLoginApi.LowFundsAlreadyDisplayed();

        return !!response;
    };

    private _autoLogin = async (message: MessageEvent, options?: ILoginOptions) => {
        this._webLoginService.Hide();
        await this._registrationAuthenticationService.autoLogin(message, options);
        this._cfCommonService.CloseWrapDiv();
    };
}
